import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Upload, Trash2, Loader2 } from "lucide-react";

function Thumb({ doc, onDelete, canDelete }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true; let obj = null;
    api.get(`/inspections/documents/${doc.id}/download`, { responseType: "blob" })
      .then((r) => { if (active) { obj = URL.createObjectURL(r.data); setUrl(obj); } }).catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [doc.id]);
  return (
    <div className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50 aspect-square" data-testid={`measure-photo-${doc.id}`}>
      {url ? <img src={url} alt={doc.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>}
      {canDelete && <button onClick={() => onDelete(doc)} data-testid={`measure-photo-del-${doc.id}`} className="absolute top-1 right-1 p-1 rounded bg-white/90 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>}
    </div>
  );
}

export default function MeasurePhotosDialog({ open, onClose, measure, canWrite }) {
  const [photos, setPhotos] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    if (!measure) return;
    api.get(`/inspections/measures/${measure.id}/photos`).then((r) => setPhotos(r.data)).catch(() => setPhotos([]));
  }, [measure]);
  useEffect(() => { if (open) { setPhotos(null); load(); } }, [open, load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/inspections/measures/${measure.id}/photos`, fd);
      toast.success("Nachweisfoto hochgeladen");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (d) => {
    try { await api.delete(`/inspections/measures/photos/${d.id}`); toast.success("Foto gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><Camera className="h-5 w-5 text-primary" />Nachweisfotos der Mängelbeseitigung</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">{measure?.title}</p>
        {canWrite && (
          <div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="measure-photo-input" onChange={(e) => upload(e.target.files[0])} />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="measure-photo-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}Foto aufnehmen / hochladen
            </Button>
          </div>
        )}
        {!photos ? <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          : photos.length === 0 ? <div className="p-6 text-center text-slate-400 text-sm">Noch keine Nachweisfotos.</div>
          : <div className="grid grid-cols-3 gap-2">{photos.map((p) => <Thumb key={p.id} doc={p} onDelete={del} canDelete={canWrite} />)}</div>}
      </DialogContent>
    </Dialog>
  );
}
